import { useMemo, type ReactNode } from "react";
import { Badge } from "@/components/ui/badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Building2 } from "lucide-react";

import type { EchoflicksListingIngest, EchoflicksSessionEvent } from "../types";
import { CoverageSummary } from "./FloorPlanCard";
import {
  buildingProfileFromSources,
  ingestRecordsOf,
  primaryIngestRecord,
  roomMapFromEvents,
  type BuildingProfileView,
  type BuildingScoredFact,
} from "./sessionUtils";

// ---------------------------------------------------------------------------
// BuildingProfileCard (BLD-043): the bot's `building_profile.v1` answer for
// what the building is (structure, subtype, local label with the accept /
// review / reject band), how many storeys it has ("1½"), the unit floor, the
// entry level, outbuildings, the evidence behind each fact with its source
// and quote, the conflicts it recorded, and whether the floor plan covers
// every floor (coverage status and gaps in plain words).
//
// Carriers, feature-detected and merged field by field (sessionUtils
// `buildingProfileFromSources`): a `property.building_profile` event, the
// listing-ingest record, and the `director.skill_room_map` subset (structure,
// subtype, label, storeys, unit_floor, band, coverage). Renders nothing when
// none of them carries a profile, so every pre-BLD Echo is unchanged.
// Copy rule: no em dashes.
// ---------------------------------------------------------------------------

const Fact = ({
  label,
  children,
  testId,
}: {
  label: string;
  children: ReactNode;
  testId?: string;
}) => (
  <div data-testid={testId}>
    <dt className="text-muted-foreground">{label}</dt>
    <dd className="font-medium">{children}</dd>
  </div>
);

const scored = (fact: BuildingScoredFact) =>
  `${fact.label}${fact.confidencePercent ? ` · ${fact.confidencePercent}` : ""}`;

const fromSource = (source: string | null) =>
  source ? ` (from ${source.replaceAll("_", " ")})` : "";

const AUTHORITY_META: Record<string, { label: string; className: string }> = {
  observe: {
    label: "observe only",
    className: "text-muted-foreground",
  },
  on: { label: "authoritative", className: "border-sky-500 text-sky-600" },
};

export const BuildingProfileFacts = ({
  profile,
}: {
  profile: BuildingProfileView;
}) => {
  const bedrooms = profile.bedrooms;
  const bedroomParts = bedrooms
    ? [
        bedrooms.value !== null
          ? `${bedrooms.value}${fromSource(bedrooms.source)}`
          : null,
        bedrooms.upper !== null ? `${bedrooms.upper} upstairs` : null,
      ].filter(Boolean)
    : [];
  return (
    <dl className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
      {profile.structure ? (
        <Fact label="Structure" testId="building-profile-structure">
          <span
            className={
              profile.structure.value === "unknown"
                ? "text-muted-foreground"
                : ""
            }
          >
            {scored(profile.structure)}
          </span>
        </Fact>
      ) : null}
      {profile.subtype ? (
        <Fact label="Subtype" testId="building-profile-subtype">
          {scored(profile.subtype)}
        </Fact>
      ) : null}
      {profile.label ? (
        <Fact label="Label" testId="building-profile-label">
          {profile.label.value ?? profile.label.localeLabel}
          {profile.label.value && profile.label.localeLabel ? (
            <span className="ml-1 font-normal text-muted-foreground">
              ({profile.label.localeLabel})
            </span>
          ) : null}
          {profile.label.confidencePercent
            ? ` · ${profile.label.confidencePercent}`
            : ""}
        </Fact>
      ) : null}
      {profile.storeys ? (
        <Fact label="Storeys" testId="building-profile-storeys">
          {profile.storeys.text}
          {profile.storeys.detail ? (
            <span className="ml-1 font-normal text-muted-foreground">
              ({profile.storeys.detail})
            </span>
          ) : null}
          {profile.storeys.confidencePercent
            ? ` · ${profile.storeys.confidencePercent}`
            : ""}
          {profile.storeysTotal ? (
            <span className="block text-xs font-normal text-muted-foreground">
              building has {profile.storeysTotal}
            </span>
          ) : null}
        </Fact>
      ) : null}
      {profile.levelsDeclared ? (
        <Fact label="Levels declared">{profile.levelsDeclared}</Fact>
      ) : null}
      {profile.unitFloor ? (
        <Fact label="Unit floor" testId="building-profile-unit-floor">
          Floor {profile.unitFloor.value}
          {fromSource(profile.unitFloor.source)}
        </Fact>
      ) : null}
      {profile.entryLevel ? (
        <Fact label="Entry level" testId="building-profile-entry-level">
          level {profile.entryLevel.ordinal}
          {fromSource(profile.entryLevel.source)}
        </Fact>
      ) : null}
      {bedroomParts.length > 0 ? (
        <Fact label="Bedrooms">{bedroomParts.join(", ")}</Fact>
      ) : null}
      {profile.levelsExpected.length > 0 ? (
        <Fact label="Levels expected">{profile.levelsExpected.join(", ")}</Fact>
      ) : null}
      {profile.outbuildings.length > 0 ? (
        <Fact label="Outbuildings" testId="building-profile-outbuildings">
          {profile.outbuildings
            .map((item) => `${item.kind}${fromSource(item.source)}`)
            .join(", ")}
        </Fact>
      ) : null}
      {profile.propertyTypeCompat ? (
        <Fact label="Property type (compatible)">
          {profile.propertyTypeCompat}
        </Fact>
      ) : null}
    </dl>
  );
};

export const BuildingProfileCard = ({
  events,
  media = [],
}: {
  events: EchoflicksSessionEvent[];
  /** Session media: the primary listing-ingest record may carry a profile. */
  media?: ReadonlyArray<{
    kind: string;
    created_at_source?: string | null;
    ingest?: EchoflicksListingIngest | null;
  }>;
}) => {
  const profile = useMemo(() => {
    const ingest = primaryIngestRecord(ingestRecordsOf(media))?.ingest ?? null;
    return buildingProfileFromSources({
      events,
      ingest,
      roomMap: roomMapFromEvents(events),
    });
  }, [events, media]);
  if (!profile) return null;

  const authority = profile.authority
    ? (AUTHORITY_META[profile.authority] ?? {
        label: profile.authority,
        className: "text-muted-foreground",
      })
    : null;

  return (
    <Card data-testid="building-profile-card">
      <CardHeader>
        <div className="flex flex-wrap items-center gap-2">
          <CardTitle className="flex items-center gap-2">
            <Building2 className="size-4" />
            Building profile
          </CardTitle>
          {profile.band ? (
            <Badge
              data-testid="building-profile-band"
              variant="outline"
              className={profile.band.className}
            >
              {profile.band.label}
            </Badge>
          ) : null}
          {authority ? (
            <Badge
              data-testid="building-profile-authority"
              variant="outline"
              className={authority.className}
            >
              {authority.label}
            </Badge>
          ) : null}
          {profile.carriers.length > 0 ? (
            <span className="ml-auto text-xs text-muted-foreground">
              from {profile.carriers.join(" + ")}
            </span>
          ) : null}
        </div>
        <CardDescription>
          What the bot resolved about the building: its type, how many floors it
          has, the evidence for each fact, and whether the floor plan shows
          every floor.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4 text-sm">
        <BuildingProfileFacts profile={profile} />

        {profile.coverage ? (
          <CoverageSummary
            coverage={profile.coverage}
            testId="building-profile-coverage"
          />
        ) : null}

        {profile.conflicts.length > 0 ? (
          <div data-testid="building-profile-conflicts" className="space-y-1">
            <div className="text-xs font-medium text-muted-foreground">
              Conflicts ({profile.conflicts.length})
            </div>
            <ul className="list-disc space-y-0.5 pl-5 text-xs text-amber-700 dark:text-amber-300">
              {profile.conflicts.map((conflict) => (
                <li key={conflict.key} data-testid="building-profile-conflict">
                  <span className="font-medium">{conflict.fact}</span>:{" "}
                  {conflict.values.join(" vs ") || "?"}
                  {conflict.sources.length > 0
                    ? ` (${conflict.sources.join(", ")})`
                    : ""}
                  {conflict.note ? (
                    <span className="text-muted-foreground">
                      . {conflict.note}
                    </span>
                  ) : null}
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {profile.evidence.length > 0 ? (
          <div
            data-testid="building-profile-evidence"
            className="overflow-x-auto rounded-md border"
          >
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b bg-muted/50 text-left text-muted-foreground">
                  <th className="px-2 py-1 font-medium">Fact</th>
                  <th className="px-2 py-1 font-medium">Value</th>
                  <th className="px-2 py-1 font-medium">Source</th>
                  <th className="px-2 py-1 font-medium">Quote</th>
                  <th className="px-2 py-1 text-right font-medium">Weight</th>
                </tr>
              </thead>
              <tbody>
                {profile.evidence.map((item) => (
                  <tr
                    key={item.key}
                    data-testid="building-profile-evidence-row"
                    className="border-b last:border-b-0"
                  >
                    <td className="px-2 py-1">{item.fact}</td>
                    <td className="px-2 py-1 font-medium">{item.value}</td>
                    <td className="px-2 py-1">{item.source}</td>
                    <td className="px-2 py-1 text-muted-foreground">
                      {item.quote ? `"${item.quote}"` : ""}
                    </td>
                    <td className="whitespace-nowrap px-2 py-1 text-right text-muted-foreground">
                      {item.weight ?? ""}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : null}

        {profile.lexiconVersion || profile.policyDigest ? (
          <div className="font-mono text-[11px] text-muted-foreground">
            {[
              profile.lexiconVersion
                ? `lexicon ${profile.lexiconVersion}`
                : null,
              profile.policyDigest
                ? `policy ${profile.policyDigest.slice(0, 12)}`
                : null,
            ]
              .filter(Boolean)
              .join(" · ")}
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
};
